<template>
  <refonte-modal
    v-bind:show="props.show"
    v-on:close="props.on_close"
    title="Ajouter au calendrier"
    class="max-w-lg">
    <template v-slot:modalBody>
      <div class="flex flex-col gap-2">
        <div class="grid grid-cols-1 gap-2 md:grid-cols-4">
          <refonte-button
            variant="secondary"
            class="flex min-h-[70px] w-full flex-col items-center justify-center gap-1 rounded-xl border border-core-300 p-2 transition hover:scale-[1.02] hover:border-primary-700 hover:shadow-sm"
            v-on:click="openCalendar('google')">
            <img
              src="@/assets/img/mail_google.png"
              alt="Google Calendar"
              class="mx-auto mb-2 h-8 w-auto object-contain" />
            <span class="text-sm font-medium text-core-900">Google</span>
          </refonte-button>

          <refonte-button
            variant="secondary"
            class="flex min-h-[70px] w-full flex-col items-center justify-center gap-1 rounded-xl border border-core-300 p-2 transition hover:scale-[1.02] hover:border-primary-700 hover:shadow-sm"
            v-on:click="openCalendar('outlook')">
            <img
              src="@/assets/img/mail_outlook.png"
              alt="Outlook Calendar"
              class="mx-auto mb-2 h-8 w-auto object-contain" />
            <span class="text-sm font-medium text-core-900">Outlook</span>
          </refonte-button>

          <refonte-button
            variant="secondary"
            class="flex min-h-[70px] w-full flex-col items-center justify-center gap-1 rounded-xl border border-core-300 p-2 transition hover:scale-[1.02] hover:border-primary-700 hover:shadow-sm"
            v-on:click="openCalendar('outlookcom')">
            <img
              src="@/assets/img/mail_outlookcom.png"
              alt="Outlook.com Calendar"
              class="mx-auto mb-2 h-8 w-auto object-contain" />
            <span class="text-sm font-medium text-core-900">Outlook.com</span>
          </refonte-button>

          <refonte-button
            variant="secondary"
            class="flex min-h-[70px] w-full flex-col items-center justify-center gap-1 rounded-xl border border-core-300 p-2 transition hover:scale-[1.02] hover:border-primary-700 hover:shadow-sm"
            v-on:click="openCalendar('apple')">
            <img
              src="@/assets/img/apple.png"
              alt="Apple Calendar"
              class="mx-auto mb-2 h-8 w-auto object-contain" />
            <span class="text-sm font-medium text-core-900">Apple</span>
          </refonte-button>
        </div>
      </div>
    </template>
  </refonte-modal>
</template>

<script setup lang="ts">
  import RefonteModal from '@/components/refonte/modals/refonte-modal.vue';
  import RefonteButton from '@/components/refonte/buttons/refonte-button.vue';

  type Site = {
    denomination?: string;
    numeroVoie?: string;
    codePostal?: string;
    ville?: string;
  };

  type CalendarProvider = 'google' | 'outlook' | 'outlookcom' | 'apple';

  type CalendarEvent = {
    title: string;
    description: string;
    location: string;
    start: Date;
    end: Date;
  };

  const props = withDefaults(
    defineProps<{
      show: boolean;
      on_close: () => void;
      class?: string;
      title: string;
      description?: Site;
      location?: string;
      start?: string;
      end?: string;
      reference?: string;
    }>(),
    {
      class: '',
      detail: undefined,
      description: undefined,
      reference: ''
    }
  );

  const pad = (value: number): string => String(value).padStart(2, '0');

  const escapeICS = (value: string): string =>
    value
      .replace(/\\/g, '\\\\')
      .replace(/\n/g, '\\n')
      .replace(/,/g, '\\,')
      .replace(/;/g, '\\;');

  const parseFrenchDate = (dateStr: string): Date => {
    if (!dateStr) return new Date('');

    const [datePart = '', timePart = '00:00:00'] = dateStr.trim().split(' ');
    const [day = '0', month = '0', year = '0'] = datePart.split('/');
    const [hours = '0', minutes = '0', seconds = '0'] = timePart.split(':');

    return new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hours),
      Number(minutes),
      Number(seconds)
    );
  };

  const formatGoogleDate = (date: Date): string =>
    date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';

  const formatOutlookDate = (date: Date): string =>
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;

  const formatAddress = (site?: Site, label = 'Adresse'): string => {
    if (!site) return '';

    const parts = [
      site.denomination,
      site.numeroVoie,
      `${site.codePostal ?? ''} ${site.ville ?? ''}`.trim()
    ].filter(Boolean);

    return parts.length ? `${label} : ${parts.join(', ')}` : '';
  };

  const buildEvent = (): CalendarEvent => {
    return {
      title: props.title ?? '',
      description: formatAddress(props.description),
      location: props.location ?? '',
      start: parseFrenchDate(props.start ?? ''),
      end: parseFrenchDate(props.end ?? '')
    };
  };

  const buildGoogleCalendarUrl = (event: CalendarEvent): string => {
    const params = new URLSearchParams({
      action: 'TEMPLATE',
      text: event.title,
      details: event.description,
      location: event.location,
      dates: `${formatGoogleDate(event.start)}/${formatGoogleDate(event.end)}`
    });

    return `https://calendar.google.com/calendar/render?${params.toString()}`;
  };

  const buildOutlookCalendarUrl = (event: CalendarEvent): string => {
    const params = new URLSearchParams({
      path: '/calendar/action/compose',
      rru: 'addevent',
      allday: 'false',
      subject: event.title,
      body: event.description,
      location: event.location,
      startdt: formatOutlookDate(event.start),
      enddt: formatOutlookDate(event.end)
    });

    return `https://outlook.office.com/calendar/0/action/compose?${params.toString()}`;
  };

  const generateICS = (event: CalendarEvent): string => {
    return [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'BEGIN:VEVENT',
      `SUMMARY:${escapeICS(event.title)}`,
      `DESCRIPTION:${escapeICS(event.description)}`,
      `LOCATION:${escapeICS(event.location)}`,
      `DTSTART:${formatGoogleDate(event.start)}`,
      `DTEND:${formatGoogleDate(event.end)}`,
      'END:VEVENT',
      'END:VCALENDAR'
    ].join('\r\n');
  };

  const openGoogleCalendar = () => {
    const url = buildGoogleCalendarUrl(buildEvent());
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const openOutlookCalendar = () => {
    const url = buildOutlookCalendarUrl(buildEvent());
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const downloadICS = () => {
    const event = buildEvent();
    const ics = generateICS(event);

    const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = `event_${props.reference}.ics`;
    link.click();

    URL.revokeObjectURL(url);
  };

  const openCalendar = (provider: CalendarProvider) => {
    const actions: Record<CalendarProvider, () => void> = {
      google: openGoogleCalendar,
      outlook: downloadICS,
      outlookcom: openOutlookCalendar,
      apple: downloadICS
    };

    actions[provider]();
  };
</script>

<style scoped lang="scss"></style>
